import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Caterpillar — a happy garden caterpillar, storybook style: a chain of plump
// green ball segments shrinking toward the tail, led by a bigger smiley head
// with big eyes and two bobble antennae, the whole train riding on tiny stub
// feet. Faces +Z (the head end), feet on y = 0. About 0.5 m long along Z.
//
// Rig layout (flat — each named node moves about its own pivot):
//   - "head"  : the big front segment + face + antennae. Bobs in idle.
//   - "seg-1" / "seg-2" / "seg-3" : the three mid segments (each with its
//     feet). They arch up and down in travelling phase — the inchworm ripple.
// The tail segments and the head's feet are static (unnamed).

const GREEN = "#8cc63f";      // fresh leaf green
const GREEN_DARK = "#689e2e"; // shaded back stripe
const CREAM = "#f6efe3";      // belly patches + highlights
const CORAL = "#e8845b";      // antenna bobbles + cheek spots
const EYE_WHITE = "#f7f3e8";  // eye whites (never pure white)
const PUPIL = "#332b33";      // dark plum pupils

// Segment centreline: z positions and radii, head first (+Z) to tail (-Z).
const HEAD_Z = 0.17, HEAD_R = 0.085;

// The pivots the animations move the named nodes about (each segment arches
// about its own ground contact).
const HEAD_PIVOT: [number, number, number] = [0, 0, HEAD_Z];
const SEG_PIVOTS: [number, number, number][] = [
  [0, 0, 0.055],
  [0, 0, -0.035],
  [0, 0, -0.12],
];

// One big friendly eye on the head: white ball, dark pupil forward, highlight.
function eye(x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.026, 10, 8), color: EYE_WHITE,
      position: [x, 0.115, HEAD_Z + 0.062], name: "head" },
    { geometry: new THREE.SphereGeometry(0.014, 8, 6), color: PUPIL,
      position: [x, 0.115, HEAD_Z + 0.080], name: "head" },
    { geometry: new THREE.SphereGeometry(0.005, 6, 5), color: CREAM,
      position: [x + 0.006, 0.122, HEAD_Z + 0.089], name: "head" },
  ];
}

// One antenna: a thin green stalk with a coral bobble, angled up and out.
function antenna(side: 1 | -1): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.006, 0.008, 0.075, 6), color: GREEN_DARK,
      position: [side * 0.035, 0.185, HEAD_Z + 0.02], rotation: [0.25, 0, -side * 0.4], name: "head" },
    { geometry: new THREE.SphereGeometry(0.015, 8, 6), color: CORAL,
      position: [side * 0.050, 0.220, HEAD_Z + 0.032], name: "head" },
  ];
}

// One body segment: a green ball with a darker back cap, a cream belly and a
// pair of tiny stub feet. `name` lets the ripple arch it about its pivot.
function segment(z: number, r: number, name?: string): Part[] {
  const cy = r + 0.005;
  return [
    { geometry: new THREE.SphereGeometry(r, 12, 10), color: GREEN,
      position: [0, cy, z], name },
    { geometry: new THREE.SphereGeometry(r * 0.82, 10, 8), color: GREEN_DARK,
      position: [0, cy + r * 0.28, z], scale: [0.95, 0.75, 0.95], name },
    { geometry: new THREE.SphereGeometry(r * 0.55, 8, 6), color: CREAM,
      position: [0, cy - r * 0.45, z], scale: [1.1, 0.6, 1.0], name },
    // Tiny stub feet either side.
    { geometry: new THREE.SphereGeometry(0.018, 8, 6), color: GREEN_DARK,
      position: [-r * 0.75, 0.014, z], scale: [1, 0.75, 1.2], name },
    { geometry: new THREE.SphereGeometry(0.018, 8, 6), color: GREEN_DARK,
      position: [r * 0.75, 0.014, z], scale: [1, 0.75, 1.2], name },
  ];
}

export const caterpillar = defineModel({
  id: "caterpillar",
  batchId: "mess",
  name: "Caterpillar",
  tags: ["mess", "cute", "animated", "creature", "insect", "caterpillar", "bug", "garden", "tiny"],
  summary: "A happy chain of green ball segments with a big smiley head and stub feet.",
  description:
    "A storybook caterpillar: a train of plump green ball segments shrinking " +
    "toward the tail, led by a bigger smiley head with bead-bright eyes, coral " +
    "bobble antennae and a proper little grin, riding on tiny stub feet. Bobs " +
    "at rest (idle) and crawls in place with an inchworm ripple (inchworm).",
  author: "models",
  footprint: 0.28,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── head (named, bobs) ───────────────────────────────────────────────
      { geometry: new THREE.SphereGeometry(HEAD_R, 14, 12), color: GREEN,
        position: [0, HEAD_R + 0.005, HEAD_Z], name: "head" },
      // A cream muzzle patch the face sits on.
      { geometry: new THREE.SphereGeometry(0.055, 10, 8), color: CREAM,
        position: [0, 0.080, HEAD_Z + 0.045], scale: [1.1, 0.9, 0.7], name: "head" },
      ...eye(0.038), ...eye(-0.038),
      // The smile: a thin dark arc turned to face forward, opening upward.
      { geometry: new THREE.TorusGeometry(0.026, 0.006, 6, 12, Math.PI), color: PUPIL,
        position: [0, 0.078, HEAD_Z + 0.082], rotation: [0, 0, Math.PI], name: "head" },
      // Two round coral cheeks either side of the smile.
      { geometry: new THREE.SphereGeometry(0.012, 8, 6), color: CORAL,
        position: [-0.052, 0.088, HEAD_Z + 0.068], scale: [1, 0.8, 0.5], name: "head" },
      { geometry: new THREE.SphereGeometry(0.012, 8, 6), color: CORAL,
        position: [0.052, 0.088, HEAD_Z + 0.068], scale: [1, 0.8, 0.5], name: "head" },
      ...antenna(1), ...antenna(-1),
      // The head's own stub feet (static, so the front stays planted).
      { geometry: new THREE.SphereGeometry(0.018, 8, 6), color: GREEN_DARK,
        position: [-0.062, 0.014, HEAD_Z], scale: [1, 0.75, 1.2] },
      { geometry: new THREE.SphereGeometry(0.018, 8, 6), color: GREEN_DARK,
        position: [0.062, 0.014, HEAD_Z], scale: [1, 0.75, 1.2] },

      // ── body segments, head to tail ──────────────────────────────────────
      ...segment(0.055, 0.072, "seg-1"),
      ...segment(-0.035, 0.068, "seg-2"),
      ...segment(-0.12, 0.062, "seg-3"),
      ...segment(-0.195, 0.054),
      ...segment(-0.26, 0.044),
    ];
  },
  animations: [
    // Idle — a contented wobble: the head bobs and tilts while a soft breath
    // rolls down the first segments.
    {
      id: "idle",
      label: "Idle bob",
      duration: 2.6,
      loop: true,
      tracks: [
        {
          target: "head", pivot: HEAD_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.3, value: [0, 0.012, 0], ease: "sine" },
            { t: 2.6, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0.03], ease: "sine" },
            { t: 1.3, value: [0, 0, -0.03], ease: "sine" },
            { t: 2.6, value: [0, 0, 0.03], ease: "sine" },
          ],
        },
        {
          target: "seg-1", pivot: SEG_PIVOTS[0],
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 1.3, value: [1.03, 1.04, 1.0], ease: "sine" },
            { t: 2.6, value: [1, 1, 1], ease: "sine" },
          ],
        },
        {
          target: "seg-2", pivot: SEG_PIVOTS[1],
          scale: [
            { t: 0, value: [1.03, 1.04, 1.0], ease: "sine" },
            { t: 1.3, value: [1, 1, 1], ease: "sine" },
            { t: 2.6, value: [1.03, 1.04, 1.0], ease: "sine" },
          ],
        },
      ],
    },

    // Inchworm — the signature move: the three mid segments arch up and settle
    // in travelling phase (a wave running head to tail), crawling in place,
    // while the head rocks along with the effort.
    {
      id: "inchworm",
      label: "Inchworm ripple",
      duration: 1.8,
      loop: true,
      tracks: [
        {
          target: "seg-1", pivot: SEG_PIVOTS[0],
          position: [
            { t: 0, value: [0, 0.030, 0], ease: "sine" },
            { t: 0.45, value: [0, 0, 0], ease: "sine" },
            { t: 0.9, value: [0, 0, 0], ease: "sine" },
            { t: 1.35, value: [0, 0, 0], ease: "sine" },
            { t: 1.8, value: [0, 0.030, 0], ease: "sine" },
          ],
        },
        {
          target: "seg-2", pivot: SEG_PIVOTS[1],
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.45, value: [0, 0.034, 0], ease: "sine" },
            { t: 0.9, value: [0, 0, 0], ease: "sine" },
            { t: 1.35, value: [0, 0, 0], ease: "sine" },
            { t: 1.8, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "seg-3", pivot: SEG_PIVOTS[2],
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.45, value: [0, 0, 0], ease: "sine" },
            { t: 0.9, value: [0, 0.030, 0], ease: "sine" },
            { t: 1.35, value: [0, 0, 0], ease: "sine" },
            { t: 1.8, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          rotation: [
            { t: 0, value: [0.06, 0, 0], ease: "sine" },
            { t: 0.45, value: [-0.04, 0, 0], ease: "sine" },
            { t: 0.9, value: [0.02, 0, 0], ease: "sine" },
            { t: 1.35, value: [-0.02, 0, 0], ease: "sine" },
            { t: 1.8, value: [0.06, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
